// src/navbars/sidebar/Sidebar.tsx

// import node module libraries
import { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { ListGroup, Card, Image, Badge } from "react-bootstrap";
import { Accordion } from "react-bootstrap";
import { CustomToggle } from "./CustomToggle";
import { CustomToggleLevelTwo } from "./CustomToggleLevelTwo";

// import simple bar scrolling used for notification item scrolling
import SimpleBar from "simplebar-react";

// import routes file
import { useDashboardMenu } from "hooks/useDashboardMenu";
import { DashboardMenuProps, ChildrenItemProps } from "types";
import { useAuth } from "context/AuthContext";

interface SidebarProps {
  showMenu: boolean;
  toggleMenu: () => void;
}

const hasActiveDescendant = (children: ChildrenItemProps[], pathname: string): boolean => {
  const decoded = decodeURIComponent(pathname);
  return children.some(
    (child) =>
      child.link === decoded ||
      (child.children != null && hasActiveDescendant(child.children, decoded))
  );
};

const Sidebar: React.FC<SidebarProps> = ({ toggleMenu }) => {
  const location = useLocation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const { user } = useAuth();
  const { menu: dynamicMenu } = useDashboardMenu(user);

  // Root accordion: alwaysOpen so active section can't be collapsed by clicking another
  const [openRootKeys, setOpenRootKeys] = useState<string[]>([]);
  // Inner accordion per root section (client groups): keyed by root menu id
  const [openInnerKeys, setOpenInnerKeys] = useState<Record<string, string | null>>({});

  useEffect(() => {
    for (const item of dynamicMenu) {
      if (!item.children?.length) continue;
      if (hasActiveDescendant(item.children, location.pathname)) {
        setOpenRootKeys((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
        item.children.forEach((child, idx) => {
          if (child.children && hasActiveDescendant(child.children, location.pathname)) {
            const key = `${item.id}-${idx}`;
            setOpenInnerKeys((prev) =>
              prev[item.id] === key ? prev : { ...prev, [item.id]: key }
            );
          }
        });
        break;
      }
    }
  }, [location.pathname, dynamicMenu]);

  const handleRootSelect = (key: string | string[] | null | undefined) => {
    // With alwaysOpen, react-bootstrap passes the complete new array of open keys
    const newKeys = Array.isArray(key) ? key : [];
    const activeRootId = dynamicMenu.find(
      (item) => item.children?.length && hasActiveDescendant(item.children, location.pathname)
    )?.id;
    // Always keep the section containing the active route open
    if (activeRootId && !newKeys.includes(activeRootId)) {
      setOpenRootKeys([...newKeys, activeRootId]);
    } else {
      setOpenRootKeys(newKeys);
    }
  };

  const handleInnerSelect = (
    rootId: string,
    key: string | string[] | null | undefined,
    activeChildKey: string | null
  ) => {
    const strKey = (key as string | null | undefined) ?? null;
    if (!strKey) {
      if (openInnerKeys[rootId] !== activeChildKey) {
        setOpenInnerKeys((prev) => ({ ...prev, [rootId]: null }));
      }
      return;
    }
    // Prevent closing the subsection that contains the active route
    if (strKey === activeChildKey && openInnerKeys[rootId] === strKey) return;
    setOpenInnerKeys((prev) => ({
      ...prev,
      [rootId]: prev[rootId] === strKey ? null : strKey,
    }));
  };

  const generateLink = (item: any) => {
    return (
      <Link
        to={item.link}
        className={`nav-link ${
          decodeURIComponent(location.pathname) === item.link ? "active" : ""
        }`}
        onClick={() => {
          if (item.onClick) item.onClick();
          if (isMobile) toggleMenu();
        }}
      >
        <span className="sidebar-link-text" title={item.name}>
          {item.name}
        </span>
        {item.badge && (
          <Badge className="ms-1" bg={item.badgecolor || "primary"}>
            {item.badge}
          </Badge>
        )}
      </Link>
    );
  };

  return (
    <Fragment>
      <SimpleBar style={{ maxHeight: "100vh" }}>
        <div className="nav-scroller">
          <Link to="/" className="navbar-brand">
            <Image
              src="/images/brand/logo/dustech_logo.webp"
              className="mb-2"
              alt=""
            />
          </Link>
        </div>
        {/* Dashboard Menu */}
        <Accordion
          alwaysOpen
          activeKey={openRootKeys}
          onSelect={handleRootSelect}
          as="ul"
          className="navbar-nav flex-column"
        >
          {dynamicMenu.map(function (menu: DashboardMenuProps, index: number) {
            if (menu.role && !menu.role.includes(user?.role ?? "")) return null;

            if (menu.grouptitle) {
              return (
                <Card bsPrefix="nav-item" key={menu.id}>
                  <div className="navbar-heading">{menu.title}</div>
                </Card>
              );
            } else {
              if (menu.children) {
                const isRootActive =
                  menu.children.length > 0 &&
                  hasActiveDescendant(menu.children, location.pathname);
                return (
                  <Fragment key={menu.id}>
                    {/* main menu / root menu level / root items */}
                    <CustomToggle eventKey={menu.id} icon={menu.icon} isActive={isRootActive}>
                      {menu.title}
                      {menu.badge ? (
                        <Badge
                          className="ms-1"
                          bg={menu.badgecolor ? menu.badgecolor : "primary"}
                        >
                          {menu.badge}
                        </Badge>
                      ) : (
                        ""
                      )}
                    </CustomToggle>
                    <Accordion.Collapse
                      eventKey={menu.id}
                      as="li"
                      bsPrefix="nav-item"
                    >
                      <ListGroup
                        as="ul"
                        bsPrefix=""
                        className="nav flex-column"
                      >
                        {menu.children.map(function (
                          menuLevel1Item,
                          menuLevel1Index
                        ) {
                          const childKey = `${menu.id}-${menuLevel1Index}`;
                          if (menuLevel1Item.children) {
                            const isLevel1Active = hasActiveDescendant(
                              menuLevel1Item.children,
                              location.pathname
                            );
                            const activeChildKey = isLevel1Active ? childKey : null;
                            return (
                              <ListGroup.Item
                                as="li"
                                bsPrefix="nav-item"
                                key={menuLevel1Item.id}
                              >
                                {/* first level menu started  */}
                                <Accordion
                                  activeKey={openInnerKeys[menu.id] ?? undefined}
                                  onSelect={(key) =>
                                    handleInnerSelect(menu.id, key, activeChildKey)
                                  }
                                  className="navbar-nav flex-column"
                                >
                                  <CustomToggleLevelTwo
                                    eventKey={childKey}
                                    isActive={isLevel1Active}
                                  >
                                    <span
                                      className="sidebar-link-text"
                                      title={menuLevel1Item.title}
                                    >
                                      {menuLevel1Item.title}
                                    </span>
                                    {menuLevel1Item.badge ? (
                                      <Badge
                                        className="ms-1"
                                        bg={
                                          menuLevel1Item.badgecolor
                                            ? menuLevel1Item.badgecolor
                                            : "primary"
                                        }
                                      >
                                        {menuLevel1Item.badge}
                                      </Badge>
                                    ) : (
                                      ""
                                    )}
                                  </CustomToggleLevelTwo>
                                  <Accordion.Collapse
                                    eventKey={childKey}
                                    bsPrefix="nav-item"
                                  >
                                    <ListGroup
                                      as="ul"
                                      bsPrefix=""
                                      className="nav flex-column"
                                    >
                                      {/* second level menu started  */}
                                      {menuLevel1Item.children.map(function (
                                        menuLevel2Item,
                                        menuLevel2Index
                                      ) {
                                        const level2Key = `${menuLevel1Index}-${menuLevel2Index}`;
                                        if (menuLevel2Item.children) {
                                          return (
                                            <ListGroup.Item
                                              as="li"
                                              bsPrefix="nav-item"
                                              key={menuLevel2Index}
                                            >
                                              {/* second level accordion menu started  */}
                                              <Accordion
                                                className="navbar-nav flex-column"
                                              >
                                                <CustomToggleLevelTwo
                                                  eventKey={level2Key}
                                                >
                                                  {menuLevel2Item.title}
                                                  {menuLevel2Item.badge ? (
                                                    <Badge
                                                      className="ms-1"
                                                      bg={
                                                        menuLevel2Item.badgecolor
                                                          ? menuLevel2Item.badgecolor
                                                          : "primary"
                                                      }
                                                    >
                                                      {menuLevel2Item.badge}
                                                    </Badge>
                                                  ) : (
                                                    ""
                                                  )}
                                                </CustomToggleLevelTwo>
                                                <Accordion.Collapse
                                                  eventKey={level2Key}
                                                  bsPrefix="nav-item"
                                                >
                                                  <ListGroup
                                                    as="ul"
                                                    bsPrefix=""
                                                    className="nav flex-column"
                                                  >
                                                    {/* third level menu started  */}
                                                    {menuLevel2Item.children.map(
                                                      function (
                                                        menuLevel3Item,
                                                        menuLevel3Index
                                                      ) {
                                                        return (
                                                          <ListGroup.Item
                                                            key={menuLevel3Index}
                                                            as="li"
                                                            bsPrefix="nav-item"
                                                          >
                                                            {generateLink(
                                                              menuLevel3Item
                                                            )}
                                                          </ListGroup.Item>
                                                        );
                                                      }
                                                    )}
                                                    {/* end of third level menu  */}
                                                  </ListGroup>
                                                </Accordion.Collapse>
                                              </Accordion>
                                              {/* end of second level accordion */}
                                            </ListGroup.Item>
                                          );
                                        } else {
                                          return (
                                            <ListGroup.Item
                                              key={menuLevel2Index}
                                              as="li"
                                              bsPrefix="nav-item"
                                            >
                                              {generateLink(menuLevel2Item)}
                                            </ListGroup.Item>
                                          );
                                        }
                                      })}
                                      {/* end of second level menu  */}
                                    </ListGroup>
                                  </Accordion.Collapse>
                                </Accordion>
                                {/* end of first level menu */}
                              </ListGroup.Item>
                            );
                          } else {
                            return (
                              <ListGroup.Item
                                as="li"
                                bsPrefix="nav-item"
                                key={menuLevel1Index}
                              >
                                {/* first level menu items */}
                                {generateLink(menuLevel1Item)}
                                {/* end of first level menu items */}
                              </ListGroup.Item>
                            );
                          }
                        })}
                      </ListGroup>
                    </Accordion.Collapse>
                    {/* end of main menu / menu level 1 / root items */}
                  </Fragment>
                );
              } else {
                return (
                  <Card bsPrefix="nav-item" key={index}>
                    {/* menu item without any children items */}
                    <Link
                      to={menu.link ?? "#"}
                      className={`nav-link ${
                        location.pathname === menu.link ? "active" : ""
                      } ${
                        menu.title === "Free Download"
                          ? "bg-primary text-white"
                          : ""
                      }`}
                    >
                      {typeof menu.icon === "string" ? (
                        <i className={`nav-icon fe fe-${menu.icon} me-2`}></i>
                      ) : (
                        menu.icon
                      )}
                      {menu.title}
                      {menu.badge ? (
                        <Badge
                          className="ms-1"
                          bg={menu.badgecolor ? menu.badgecolor : "primary"}
                        >
                          {menu.badge}
                        </Badge>
                      ) : (
                        ""
                      )}
                    </Link>
                    {/* end of menu item without any children items */}
                  </Card>
                );
              }
            }
          })}
        </Accordion>
        {/* end of Dashboard Menu */}
      </SimpleBar>
    </Fragment>
  );
};

export default Sidebar;
